import React, { useEffect, useState, useRef } from "react";
import { Project, formatDateRange, getRelatedPosts } from "../utils/projects";
import { PostMetadata } from "../utils/posts";
import { extractTableOfContents, TableOfContentsItem } from "../utils/posts";
import { useFootnoteNavigation } from "../utils/footnotes";
import ReadingProgress from "./ReadingProgress";
import Prism from "prismjs";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-jsx";
import "prismjs/components/prism-tsx";
import "prismjs/components/prism-css";
import "prismjs/components/prism-bash";
import "prismjs/components/prism-rust";
import "prismjs/components/prism-json";
import "prismjs/components/prism-lua";
import "prismjs/components/prism-csharp";

interface ProjectDetailProps {
  project: Project;
  onBack: () => void;
  onNavigate: (page: string, slug?: string) => void;
}

export default function ProjectDetail({ project, onBack, onNavigate }: ProjectDetailProps) {
  const [toc, setToc] = useState<TableOfContentsItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [relatedPosts, setRelatedPosts] = useState<PostMetadata[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = `${project.title} — Tadej Gungl`;
  }, [project.title]);

  useEffect(() => {
    if (contentRef.current) {
      const tocItems = extractTableOfContents(project.htmlContent);
      setToc(tocItems);

      const headings = contentRef.current.querySelectorAll("h2, h3");
      headings.forEach((heading) => {
        const text = heading.textContent || "";
        const id = text.toLowerCase().replace(/[^\w]+/g, "-");
        heading.id = id;
      });

      Prism.highlightAllUnder(contentRef.current);

      const scrollContainer = document.getElementById("scroll-container");
      const observer = new IntersectionObserver(
        (entries) => {
          const visibleEntries = entries.filter(entry => entry.isIntersecting);
          if (visibleEntries.length > 0) {
            const topEntry = visibleEntries.reduce((prev, current) => {
              return prev.boundingClientRect.top < current.boundingClientRect.top ? prev : current;
            });
            setActiveId(topEntry.target.id);
          }
        },
        {
          root: scrollContainer,
          rootMargin: "-120px 0px -70% 0px",
          threshold: [0, 0.5, 1],
        }
      );

      headings.forEach((heading) => observer.observe(heading));
      return () => {
        headings.forEach((heading) => observer.unobserve(heading));
      };
    }
  }, [project.htmlContent]);

  useEffect(() => {
    getRelatedPosts(project.slug).then(setRelatedPosts);
  }, [project.slug]);

  useFootnoteNavigation(contentRef, project.htmlContent);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const scrollContainer = document.getElementById("scroll-container");
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const targetScroll = scrollContainer.scrollTop + elementRect.top - containerRect.top - 100;
        scrollContainer.scrollTo({ top: targetScroll, behavior: "smooth" });
      }
    }
  };

  const DevelopmentPeriod = () => (
    <span className="label-caps">{formatDateRange(project.startDate)}</span>
  );

  const StatusBadge = () => (
    <span className="label-caps flex items-center gap-1.5">
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${
        project.status === "active" ? "bg-Green" : "bg-Overlay0"
      }`}></span>
      {project.status === "active" ? "Active" : "Archived"}
    </span>
  );

  const GitHubLink = ({ variant = "inline" }: { variant?: "inline" | "button" }) => (
    <a
      href={project.github}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-Text border-b hairline pb-1 hover:text-Sapphire hover:border-Sapphire transition-colors duration-200"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
      </svg>
      <span>Source</span>
    </a>
  );

  return (
    <div className="min-h-screen w-full bg-Base text-Text pt-16">
      <ReadingProgress />
      <div className="px-6 md:px-12 xl:grid xl:grid-cols-[13rem_minmax(0,46rem)_13rem] xl:justify-center xl:gap-12">
        {/* Table of Contents - Left Rail */}
        <aside className="hidden xl:block">
          {toc.length > 0 && (
            <div className="border-l hairline pl-4 mt-28">
              <h3 className="label-caps mb-4">Contents</h3>
              <nav className="space-y-2">
                {toc.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`
                      block w-full text-left text-sm font-mono transition-colors duration-200 cursor-pointer py-0.5
                      ${item.level === 3 ? "pl-4" : ""}
                      ${
                        activeId === item.id
                          ? "text-Sapphire"
                          : "text-Subtext0 hover:text-Text"
                      }
                    `}
                  >
                    {item.text}
                  </button>
                ))}
              </nav>
            </div>
          )}
        </aside>

        <div className="max-w-3xl mx-auto w-full min-w-0 xl:mx-0 xl:max-w-none">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="mt-8 mb-12 font-mono text-xs uppercase tracking-[0.15em] text-Subtext0 hover:text-Sapphire transition-colors cursor-pointer"
        >
          ← Back to Portfolio
        </button>

        {/* Header */}
        <header className="mb-12 border-b hairline pb-10">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
            <div className="flex-1">
              <h1 className="font-display font-medium text-3xl md:text-5xl text-Text leading-tight mb-4">
                {project.title}
              </h1>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <DevelopmentPeriod />
                <StatusBadge />
                <span className="label-caps">{project.tags.join(" · ")}</span>
              </div>
            </div>
            <GitHubLink variant="button" />
          </div>

          <p className="text-Subtext0 font-sans text-base md:text-lg mt-6 max-w-3xl">
            {project.description}
          </p>
        </header>

        {/* Article Content */}
        <article className="min-w-0 pb-16">
          <div
            ref={contentRef}
            className="prose-brutalist"
            dangerouslySetInnerHTML={{ __html: project.htmlContent }}
          />
        </article>

        {/* Related Blog Posts */}
        {relatedPosts.length > 0 && (
          <section className="mb-16 border-t hairline pt-12">
            <h2 className="font-display font-medium text-2xl text-Text mb-8">Related Posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              {relatedPosts.map((post) => (
                <button
                  key={post.slug}
                  onClick={() => onNavigate("blog", post.slug)}
                  className="group text-left border-t hairline pt-5 cursor-pointer"
                >
                  <time className="label-caps block mb-2">
                    {new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "short" })}
                  </time>
                  <h3 className="font-display font-medium text-lg text-Text mb-1 group-hover:text-Sapphire transition-colors">{post.title}</h3>
                  <p className="text-Subtext0 font-sans text-sm">{post.description}</p>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Bottom GitHub Link */}
        <div className="pb-12 flex flex-col sm:flex-row items-start sm:items-center gap-6 border-t hairline pt-8 mb-8">
          <div className="flex-1">
            <p className="font-sans text-sm text-Subtext0">
              Interested in the code? View the full repository on GitHub.
            </p>
          </div>
          <GitHubLink variant="button" />
          <button
            onClick={onBack}
            className="font-mono text-xs uppercase tracking-[0.15em] text-Subtext0 hover:text-Sapphire transition-colors cursor-pointer"
          >
            ← Back to Portfolio
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
